import 'vue';
import { ref, getCurrentInstance, onUnmounted, watch, onMounted, computed } from '@vue/composition-api';

function useTimeout(interval, startRightNow) {
    if (interval === void 0) { interval = 1000; }
    if (startRightNow === void 0) { startRightNow = true; }
    var ready = ref(false);
    var timer = null;
    function stop() {
        ready.value = false;
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }
    function start() {
        stop();
        timer = setTimeout(function () {
            ready.value = true;
            timer = null;
        }, interval);
    }
    if (startRightNow)
        start();
    if (getCurrentInstance())
        onUnmounted(stop);
    return { ready: ready, start: start, stop: stop };
}

function useTimeoutFn(cb, interval) {
    var _a = useTimeout(interval), ready = _a.ready, start = _a.start, stop = _a.stop;
    watch(ready, function (maturity) {
        maturity && cb();
    }, { lazy: true });
    return { ready: ready, start: start, stop: stop };
}

function useAsyncState(promise, defaultState, delay, catchFn) {
    if (delay === void 0) { delay = 0; }
    if (catchFn === void 0) { catchFn = function (e) { }; }
    var state = ref(defaultState);
    var ready = ref(false);
    function run() {
        promise
            .then(function (data) {
            // @ts-ignore
            state.value = data;
            ready.value = true;
        })
            .catch(catchFn);
    }
    if (!delay)
        run();
    else
        useTimeoutFn(run, delay);
    return { state: state, ready: ready };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
var events = ['chargingchange', 'chargingtimechange', 'dischargingtimechange', 'levelchange'];
function useBattery() {
    var charging = ref(false);
    var chargingTime = ref(0);
    var dischargingTime = ref(0);
    var level = ref(1);
    var supported = ref('getBattery' in navigator);
    function updateBatteryInfo() {
        charging.value = this.charging;
        chargingTime.value = this.chargingTime || 0;
        dischargingTime.value = this.dischargingTime || 0;
        level.value = this.level;
    }
    onMounted(function () {
        if (!supported.value)
            return;
        navigator.getBattery().then(function (battery) {
            updateBatteryInfo.call(battery);
            events.forEach(function (evt) {
                battery.addEventListener(evt, updateBatteryInfo);
            });
        });
    });
    onUnmounted(function () {
        if (!supported.value)
            return;
        navigator.getBattery().then(function (battery) {
            events.forEach(function (evt) {
                battery.removeEventListener(evt, updateBatteryInfo);
            });
        });
    });
    return {
        charging: charging,
        chargingTime: chargingTime,
        dischargingTime: dischargingTime,
        level: level,
        supported: supported,
    };
}

function useEventListener(type, listener, options, target) {
    if (target === void 0) { target = window; }
    onMounted(function () {
        target.addEventListener(type, listener, options);
    });
    onUnmounted(function () {
        target.removeEventListener(type, listener, options);
    });
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useBrowserLocation(target) {
    if (target === void 0) { target = ref(document.body); }
    var buildState = function (trigger) {
        var state = history.state, length = history.length;
        var hash = location.hash, host = location.host, hostname = location.hostname, href = location.href, origin = location.origin, pathname = location.pathname, port = location.port, protocol = location.protocol, search = location.search;
        return {
            trigger: trigger,
            state: state,
            length: length,
            hash: hash,
            host: host,
            hostname: hostname,
            href: href,
            origin: origin,
            pathname: pathname,
            port: port,
            protocol: protocol,
            search: search,
        };
    };
    var state = ref(buildState('load'));
    useEventListener('popstate', function () { return state.value = buildState('popstate'); });
    useEventListener('pushstate', function () { return state.value = buildState('pushstate'); });
    useEventListener('replacestate', function () { return state.value = buildState('replacestate'); });
    watch(function () { return state; }, function () {
        console.log(state);
    }, { lazy: true });
    return state;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useClipboard() {
    var text = ref('');
    var supported = ref('clipboard' in window.navigator);
    useEventListener('copy', function () {
        window.navigator.clipboard.readText().then(function (value) {
            text.value = value;
        });
    });
    function copy(txt) {
        text.value = txt;
        return window.navigator.clipboard.writeText(txt);
    }
    return {
        text: text,
        copy: copy,
        supported: supported,
    };
}

function useCounter(initialValue) {
    if (initialValue === void 0) { initialValue = 0; }
    var count = ref(initialValue);
    var inc = function (delta) {
        if (delta === void 0) { delta = 1; }
        return (count.value += delta);
    };
    var dec = function (delta) {
        if (delta === void 0) { delta = 1; }
        return (count.value -= delta);
    };
    var get = function () { return count.value; };
    var set = function (val) { return (count.value = val); };
    var reset = function (val) {
        if (val === void 0) { val = initialValue; }
        initialValue = val;
        return set(val);
    };
    return { count: count, inc: inc, dec: dec, get: get, set: set, reset: reset };
}

function useCssVar(prop, refEl) {
    var varRef = ref('');
    var el = computed(function () { var _a; return ((_a = refEl) === null || _a === void 0 ? void 0 : _a.value) || document.documentElement; });
    onMounted(function () {
        // @ts-ignore
        varRef.value = getComputedStyle(el.value).getPropertyValue(prop);
    });
    watch(varRef, function (val) {
        // @ts-ignore
        el.value && el.value.style && el.value.style.setProperty(prop, val);
    }, { lazy: true });
    return varRef;
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useDeviceLight() {
    var light = ref(null);
    useEventListener('devicelight', function (event) {
        light.value = event.value;
    });
    return light;
}

/**
 * Throttle execution of a function. Especially useful for rate limiting
 * execution of handlers on events like resize and scroll.
 *
 * @param  {Number}    delay          A zero-or-greater delay in milliseconds. For event callbacks, values around 100 or 250 (or even higher) are most useful.
 * @param  {Boolean}   [noTrailing]   Optional, defaults to false. If noTrailing is true, callback will only execute every `delay` milliseconds while the
 *                                    throttled-function is being called. If noTrailing is false or unspecified, callback will be executed one final time
 *                                    after the last throttled-function call. (After the throttled-function has not been called for `delay` milliseconds,
 *                                    the internal counter is reset)
 * @param  {Function}  callback       A function to be executed after delay milliseconds. The `this` context and all arguments are passed through, as-is,
 *                                    to `callback` when the throttled-function is executed.
 * @param  {Boolean}   [debounceMode] If `debounceMode` is true (at begin), schedule `clear` to execute after `delay` ms. If `debounceMode` is false (at end),
 *                                    schedule `callback` to execute after `delay` ms.
 *
 * @return {Function}  A new, throttled, function.
 */
function throttle(delay, noTrailing, callback, debounceMode) {
    /*
     * After wrapper has stopped being called, this timeout ensures that
     * `callback` is executed at the proper times in `throttle` and `end`
     * debounce modes.
     */
    var timeoutID;
    var cancelled = false;
    // Keep track of the last time `callback` was executed.
    var lastExec = 0;
    // Function to clear existing timeout
    function clearExistingTimeout() {
        if (timeoutID)
            clearTimeout(timeoutID);
    }
    // Function to cancel next exec
    function cancel() {
        clearExistingTimeout();
        cancelled = true;
    }
    // `noTrailing` defaults to falsy.
    if (typeof noTrailing !== 'boolean') {
        debounceMode = !!callback;
        callback = noTrailing;
        noTrailing = undefined;
    }
    /*
     * The `wrapper` function encapsulates all of the throttling / debouncing
     * functionality and when executed will limit the rate at which `callback`
     * is executed.
     */
    function wrapper() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        var self = this;
        var elapsed = Date.now() - lastExec;
        if (cancelled)
            return;
        // Execute `callback` and update the `lastExec` timestamp.
        var exec = function () {
            lastExec = Date.now();
            if (callback)
                callback.apply(self, args);
        };
        /*
         * If `debounceMode` is true (at begin) this is used to clear the flag
         * to allow future `callback` executions.
         */
        function clear() {
            timeoutID = undefined;
        }
        if (debounceMode && !timeoutID) {
            /*
             * Since `wrapper` is being called for the first time and
             * `debounceMode` is true (at begin), execute `callback`.
             */
            exec();
        }
        clearExistingTimeout();
        if (debounceMode === undefined && elapsed > delay) {
            /*
             * In throttle mode, if `delay` time has been exceeded, execute
             * `callback`.
             */
            exec();
        }
        else if (noTrailing !== true) {
            /*
             * In trailing throttle mode, since `delay` time has not been
             * exceeded, schedule `callback` to execute `delay` ms after most
             * recent execution.
             *
             * If `debounceMode` is true (at begin), schedule `clear` to execute
             * after `delay` ms.
             *
             * If `debounceMode` is false (at end), schedule `callback` to
             * execute after `delay` ms.
             */
            timeoutID = setTimeout(debounceMode ? clear : exec, debounceMode === undefined ? delay - elapsed : delay);
        }
    }
    wrapper.cancel = cancel;
    // Return the wrapper function.
    return wrapper;
}

var isClient = typeof window === 'object';
var timestamp = function () { return +Date.now(); };

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useDeviceMotion(options) {
    if (options === void 0) { options = { throttleMs: 10 }; }
    var acceleration = ref({ x: null, y: null, z: null });
    var rotationRate = ref({ alpha: null, beta: null, gamma: null });
    var interval = ref(0);
    var accelerationIncludingGravity = ref({
        x: null,
        y: null,
        z: null,
    });
    function onDeviceMotion(event) {
        acceleration.value = event.acceleration;
        accelerationIncludingGravity.value = event.accelerationIncludingGravity;
        rotationRate.value = event.rotationRate;
        interval.value = event.interval;
    }
    var handler = options.throttleMs ? throttle(options.throttleMs, onDeviceMotion) : onDeviceMotion;
    useEventListener('devicemotion', handler, false);
    return {
        acceleration: acceleration,
        accelerationIncludingGravity: accelerationIncludingGravity,
        rotationRate: rotationRate,
        interval: interval,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useDeviceOrientation() {
    var isAbsolute = ref(false);
    var alpha = ref(0);
    var beta = ref(0);
    var gamma = ref(0);
    useEventListener('deviceorientation', function (event) {
        isAbsolute.value = event.absolute;
        alpha.value = event.alpha;
        beta.value = event.beta;
        gamma.value = event.gamma;
    });
    return {
        isAbsolute: isAbsolute,
        alpha: alpha,
        beta: beta,
        gamma: gamma,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useFullscreen(target) {
    if (target === void 0) { target = ref(document.body); }
    var isFullscreen = ref(false);
    function exitFullscreen() {
        if (document.fullscreenElement)
            document.exitFullscreen();
        isFullscreen.value = false;
    }
    function enterFullscreen() {
        exitFullscreen();
        target.value.requestFullscreen().then(function () {
            isFullscreen.value = true;
        });
    }
    return {
        isFullscreen: isFullscreen,
        enterFullscreen: enterFullscreen,
        exitFullscreen: exitFullscreen,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useGeolocation(options) {
    if (options === void 0) { options = {
        enableHighAccuracy: true,
        maximumAge: 30000,
        timeout: 27000,
    }; }
    var locatedAt = ref(null);
    var error = ref(null);
    var coords = ref({
        accuracy: 0,
        latitude: 0,
        longitude: 0,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null,
    });
    function updatePosition(position) {
        locatedAt.value = position.timestamp;
        coords.value = position.coords;
        error.value = null;
    }
    var watcher;
    onMounted(function () {
        if ('geolocation' in navigator) {
            watcher = window.navigator.geolocation.watchPosition(updatePosition, function (err) {
                error.value = err;
            }, options);
        }
    });
    onUnmounted(function () {
        if (watcher)
            window.navigator.geolocation.clearWatch(watcher);
    });
    return {
        coords: coords,
        locatedAt: locatedAt,
        error: error,
    };
}

var defaultEvents = ['mousemove', 'mousedown', 'resize', 'keydown', 'touchstart', 'wheel'];
var oneMinute = 60e3;
function useIdle(ms, initialState, listeningEvents, throttleDelay) {
    if (ms === void 0) { ms = oneMinute; }
    if (initialState === void 0) { initialState = false; }
    if (listeningEvents === void 0) { listeningEvents = defaultEvents; }
    if (throttleDelay === void 0) { throttleDelay = 50; }
    var idle = ref(initialState);
    var lastActive = ref(timestamp());
    var timeout;
    var set = function (newState) {
        idle.value = newState;
    };
    var onEvent = throttle(throttleDelay, function () {
        idle.value = false;
        clearTimeout(timeout);
        timeout = setTimeout(function () { return idle.value = true; }, ms);
        lastActive.value = timestamp();
    });
    for (var _i = 0, listeningEvents_1 = listeningEvents; _i < listeningEvents_1.length; _i++) {
        var eventName = listeningEvents_1[_i];
        useEventListener(eventName, onEvent);
    }
    useEventListener('visibilitychange', function () {
        if (!document.hidden)
            onEvent();
    }, undefined, document);
    onMounted(function () {
        timeout = setTimeout(function () { return set(true); }, ms);
    });
    return { idle: idle, lastActive: lastActive };
}

function useInterval(interval, startRightNow) {
    if (interval === void 0) { interval = 1000; }
    if (startRightNow === void 0) { startRightNow = true; }
    var timer = null;
    var counter = ref(0);
    function stop() {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    }
    function start() {
        stop();
        timer = setInterval(function () {
            counter.value += 1;
        }, interval);
    }
    if (startRightNow)
        onMounted(start);
    if (getCurrentInstance())
        onUnmounted(stop);
    return { counter: counter, start: start, stop: stop };
}

function useIntervalFn(cb, interval, startRightNow) {
    if (interval === void 0) { interval = 1000; }
    if (startRightNow === void 0) { startRightNow = true; }
    var timer = null;
    function stop() {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    }
    function start() {
        stop();
        timer = setInterval(cb, interval);
    }
    if (startRightNow)
        start();
    if (getCurrentInstance())
        onUnmounted(stop);
    return { start: start, stop: stop };
}

function useStoragePlain(key, defaultValue, storage) {
    if (storage === void 0) { storage = localStorage; }
    var data = ref(defaultValue);
    function read() {
        try {
            var rawValue = storage.getItem(key);
            if (rawValue === undefined && defaultValue) {
                rawValue = String(defaultValue);
                storage.setItem(key, rawValue);
            }
            else {
                if (defaultValue == null)
                    data.value = rawValue;
                else if (typeof defaultValue === 'boolean')
                    // @ts-ignore
                    data.value = defaultValue === 'true';
                // @ts-ignore
                else if (typeof defaultValue === 'string')
                    // @ts-ignore
                    data.value = rawValue != null ? rawValue : defaultValue;
                // @ts-ignore
                else if (!Number.isNaN(defaultValue))
                    // @ts-ignore
                    data.value = rawValue != null ? Number.parseFloat(rawValue) : defaultValue;
                else
                    // @ts-ignore
                    data.value = rawValue != null ? rawValue : defaultValue;
            }
        }
        catch (e) {
            console.warn(e);
        }
    }
    read();
    useEventListener('storage', read);
    watch(data, function () {
        try {
            if (data.value == null)
                storage.removeItem(key);
            else
                storage.setItem(key, String(data.value));
        }
        catch (e) {
            console.warn(e);
        }
    }, { flush: 'sync', lazy: true });
    return data;
}

function useStorage(key, defaultValue, storage) {
    if (storage === void 0) { storage = localStorage; }
    function stringify(data) {
        return data ? JSON.stringify(data) : '';
    }
    function parse(str) {
        return str ? JSON.parse(str) : (defaultValue || {});
    }
    var plain = useStoragePlain(key, stringify(defaultValue), storage);
    var state = ref(parse(plain.value));
    function update() {
        plain.value = stringify(state.value);
    }
    watch(plain, function () { return state.value = parse(plain.value); }, { flush: 'sync', lazy: true });
    watch(state, update, { flush: 'sync', lazy: true, deep: true });
    return state;
}

function useLocalStorage(key, defaultValue) {
    return useStorage(key, defaultValue, localStorage);
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useMediaQuery(query) {
    var mediaQuery;
    // try to fetch initial value (avoid SSR issues)
    if (typeof window !== 'undefined')
        mediaQuery = window.matchMedia(query);
    var matches = ref(mediaQuery ? mediaQuery.matches : false);
    function handler(event) {
        matches.value = event.matches;
    }
    onMounted(function () {
        if (!mediaQuery)
            mediaQuery = window.matchMedia(query);
        matches.value = mediaQuery.matches;
        mediaQuery.addListener(handler);
    });
    onUnmounted(function () {
        mediaQuery.removeListener(handler);
    });
    return matches;
}

function useMouse(options) {
    if (options === void 0) { options = {}; }
    var _a = options.touch, touch = _a === void 0 ? true : _a, _b = options.resetOnTouchEnds, resetOnTouchEnds = _b === void 0 ? true : _b, _c = options.initial, initial = _c === void 0 ? { x: 0, y: 0 } : _c;
    var x = ref(initial.x);
    var y = ref(initial.y);
    useEventListener('mousemove', function (event) {
        x.value = event.pageX;
        y.value = event.pageY;
    });
    if (touch) {
        useEventListener('touchmove', function (event) {
            if (event.touches.length > 0) {
                x.value = event.touches[0].clientX;
                y.value = event.touches[0].clientY;
            }
        });
        if (resetOnTouchEnds) {
            useEventListener('touchend', function () {
                x.value = initial.x;
                y.value = initial.y;
            });
        }
    }
    return {
        x: x,
        y: y,
    };
}

function useMouseInElement(target, options) {
    if (target === void 0) { target = ref(document.body); }
    if (options === void 0) { options = {}; }
    var _a = options.handleOutside, handleOutside = _a === void 0 ? false : _a, _b = options.touch, touch = _b === void 0 ? true : _b;
    var x = ref(0);
    var y = ref(0);
    var elementX = ref(0);
    var elementY = ref(0);
    var elementPositionX = ref(0);
    var elementPositionY = ref(0);
    var elementHeight = ref(0);
    var elementWidth = ref(0);
    var isOutside = ref(false);
    var stop = watch(target, function (el, prevEl, onCleanup) {
        var moveHandler = function (event) {
            var ele = el || document.body;
            var _a = ele.getBoundingClientRect(), left = _a.left, top = _a.top, width = _a.width, height = _a.height;
            if (event instanceof TouchEvent && event.touches.length <= 0)
                return;
            x.value = event instanceof MouseEvent ? event.pageX : event.touches[0].clientX;
            y.value = event instanceof MouseEvent ? event.pageX : event.touches[0].clientX;
            elementPositionX.value = left + window.pageXOffset;
            elementPositionY.value = top + window.pageYOffset;
            elementHeight.value = height;
            elementWidth.value = width;
            var elX = x.value - elementPositionX.value;
            var elY = x.value - elementPositionY.value;
            isOutside.value = elX < 0 || elY < 0 || elX > elementWidth.value || elY > elementHeight.value;
            if (handleOutside || !isOutside.value) {
                elementX.value = elX;
                elementY.value = elY;
            }
        };
        document.addEventListener('mousemove', moveHandler);
        if (touch)
            document.addEventListener('touchmove', moveHandler);
        onCleanup(function () {
            document.removeEventListener('mousemove', moveHandler);
            if (touch)
                document.removeEventListener('touchmove', moveHandler);
        });
    });
    return {
        x: x,
        y: y,
        elementX: elementX,
        elementY: elementY,
        elementPositionX: elementPositionX,
        elementPositionY: elementPositionY,
        elementHeight: elementHeight,
        elementWidth: elementWidth,
        isOutside: isOutside,
        stop: stop,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useNetwork() {
    var isOnline = ref(true);
    var saveData = ref(false);
    var offlineAt = ref(undefined);
    var downlink = ref(undefined);
    var downlinkMax = ref(undefined);
    var effectiveType = ref(undefined);
    var type = ref('unknown');
    var navigator = window.navigator;
    var connection = 'connection' in navigator ? navigator.connection : undefined;
    function updateNetworkInformation() {
        isOnline.value = navigator.onLine;
        offlineAt.value = isOnline.value ? undefined : Date.now();
        if (!connection)
            return;
        downlink.value = connection.downlink;
        downlinkMax.value = connection.downlinkMax;
        effectiveType.value = connection.effectiveType;
        saveData.value = connection.saveData;
        type.value = connection.type;
    }
    onMounted(updateNetworkInformation);
    useEventListener('offline', function () {
        isOnline.value = false;
        offlineAt.value = Date.now();
    });
    useEventListener('online', function () {
        isOnline.value = true;
    });
    if (connection)
        useEventListener('change', updateNetworkInformation, false, connection);
    return {
        isOnline: isOnline,
        saveData: saveData,
        offlineAt: offlineAt,
        downlink: downlink,
        downlinkMax: downlinkMax,
        effectiveType: effectiveType,
        type: type,
    };
}

function getTimestamp() {
    return +Date.now();
}
function useNow() {
    var now = ref(getTimestamp());
    var started = false;
    var update = function () {
        requestAnimationFrame(function () {
            now.value = getTimestamp();
            if (started)
                update();
        });
    };
    var start = function () {
        if (!started) {
            started = true;
            update();
        }
    };
    var stop = function () {
        started = false;
    };
    start();
    if (getCurrentInstance())
        onUnmounted(function () { return stop(); });
    return now;
}

function useOnline() {
    var isOnline = useNetwork().isOnline;
    return isOnline;
}

function usePageLeave() {
    var isLeft = ref(false);
    var handler = function (event) {
        event = event || window.event;
        // @ts-ignore
        var from = event.relatedTarget || event.toElement;
        isLeft.value = !from;
    };
    useEventListener('mouseout', handler);
    return isLeft;
}

function useParallax(options) {
    var _a, _b, _c, _d, _e;
    var _f = useDeviceOrientation(), deviceBeta = _f.beta, deviceGamma = _f.gamma;
    var _g = useMouseInElement((_a = options) === null || _a === void 0 ? void 0 : _a.targetElement, { handleOutside: false }), elementX = _g.elementX, elementY = _g.elementY, elementWidth = _g.elementWidth, elementHeight = _g.elementHeight;
    var source = computed(function () {
        if (deviceBeta.value != null && deviceBeta.value != null)
            return 'deviceOrientation';
        return 'mouse';
    });
    var deviceOrientationTiltAdjust = ((_b = options) === null || _b === void 0 ? void 0 : _b.deviceOrientationTiltAdjust) || (function (i) { return i; });
    var deviceOrientationRollAdjust = ((_c = options) === null || _c === void 0 ? void 0 : _c.deviceOrientationRollAdjust) || (function (i) { return i; });
    var mouseTiltAdjust = ((_d = options) === null || _d === void 0 ? void 0 : _d.mouseTiltAdjust) || (function (i) { return i; });
    var mouseRollAdjust = ((_e = options) === null || _e === void 0 ? void 0 : _e.mouseRollAdjust) || (function (i) { return i; });
    var roll = computed(function () {
        if (source.value === 'deviceOrientation' && deviceBeta.value != null) {
            var value = -deviceBeta.value / 180;
            return deviceOrientationRollAdjust(value);
        }
        else {
            var value = -(elementY.value - elementHeight.value / 2) / elementHeight.value;
            return mouseRollAdjust(value);
        }
    });
    var tilt = computed(function () {
        if (source.value === 'deviceOrientation' && deviceGamma.value != null) {
            var value = deviceGamma.value / 180;
            return deviceOrientationTiltAdjust(value);
        }
        else {
            var value = (elementX.value - elementWidth.value / 2) / elementWidth.value;
            return mouseTiltAdjust(value);
        }
    });
    return { roll: roll, tilt: tilt, source: source };
}

var noop = function () { };
function usePermission(permissionDesc) {
    var permissionStatus = null;
    var desc = typeof permissionDesc === 'string'
        ? { name: permissionDesc }
        : permissionDesc;
    var state = ref('');
    var onChange = function () {
        if (permissionStatus)
            state.value = permissionStatus.state;
    };
    if ('permissions' in navigator) {
        navigator.permissions
            .query(desc)
            .then(function (status) {
            permissionStatus = status;
            onChange();
            useEventListener('change', onChange, undefined, permissionStatus);
        })
            .catch(noop);
    }
    return state;
}

function usePreferredColorScheme() {
    var isLight = useMediaQuery('(prefers-color-scheme: light)');
    var isDark = useMediaQuery('(prefers-color-scheme: dark)');
    return computed(function () {
        if (isDark.value)
            return 'dark';
        if (isLight.value)
            return 'light';
        return 'no-preference';
    });
}

function usePreferredDark() {
    return useMediaQuery('(prefers-color-scheme: dark)');
}

function usePreferredLanguages() {
    var value = ref(navigator.languages);
    useEventListener('languagechange', function () {
        value.value = navigator.languages;
    });
    return value;
}

function useRaf() {
    var now = useNow();
    var startAt = ref(now.value);
    var elapsed = computed(function () {
        return now.value - startAt.value;
    });
    return elapsed;
}

function useRafFn(fn, options) {
    if (options === void 0) { options = {}; }
    var _a = options.startNow, startNow = _a === void 0 ? true : _a;
    var started = false;
    function loop() {
        if (!started)
            return;
        fn();
        requestAnimationFrame(loop);
    }
    function start() {
        if (!started) {
            started = true;
            loop();
        }
    }
    function stop() {
        started = false;
    }
    if (startNow)
        start();
    if (getCurrentInstance())
        onUnmounted(function () { return stop(); });
    return { stop: stop, start: start };
}

function useSessionStorage(key, defaultValue) {
    return useStorage(key, defaultValue, sessionStorage);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function useShare(shareOpts) {
    return __awaiter(this, void 0, void 0, function () {
        var _navigator, granted;
        return __generator(this, function (_a) {
            _navigator = window.navigator;
            if (_navigator && _navigator.share) {
                granted = true;
                if (shareOpts.files && _navigator.canShare)
                    granted = _navigator.canShare({ files: shareOpts.files });
                if (granted)
                    return [2 /*return*/, _navigator.share(shareOpts)];
            }
            return [2 /*return*/];
        });
    });
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useWebSocket(url) {
    var data = ref(null);
    var state = ref('CONNECTING');
    var ws;
    var close = function close(code, reason) {
        if (!ws)
            return;
        ws.close(code, reason);
    };
    var send = function send(data) {
        if (!ws)
            return;
        ws.send(data);
    };
    onMounted(function () {
        ws = new WebSocket(url);
        ws.onopen = function () {
            state.value = 'OPEN';
        };
        ws.onclose = ws.onerror = function () {
            state.value = 'CLOSED';
        };
        ws.onmessage = function (e) {
            data.value = e.data;
        };
    });
    onUnmounted(function () {
        ws.close();
    });
    return {
        data: data,
        state: state,
        close: close,
        send: send,
    };
}

/* this implementation is original ported from https://github.com/logaretm/vue-use-web by Abdelrahman Awad */
function useWebWorker(url) {
    var data = ref(null);
    var worker;
    var post = function post(val) {
        if (!worker)
            return;
        worker.postMessage(val);
    };
    var terminate = function terminate() {
        if (!worker)
            return;
        worker.terminate();
    };
    onMounted(function () {
        worker = new Worker(url);
        worker.onmessage = function (e) {
            data.value = e.data;
        };
    });
    onUnmounted(function () {
        worker.terminate();
    });
    return {
        data: data,
        post: post,
        terminate: terminate,
    };
}

function useWindowScroll() {
    var x = ref(isClient ? window.pageXOffset : 0);
    var y = ref(isClient ? window.pageYOffset : 0);
    useEventListener('scroll', function () {
        x.value = window.pageXOffset;
        y.value = window.pageYOffset;
    }, {
        capture: false,
        passive: true,
    });
    return { x: x, y: y };
}

function useWindowSize(initialWidth, initialHeight) {
    if (initialWidth === void 0) { initialWidth = Infinity; }
    if (initialHeight === void 0) { initialHeight = Infinity; }
    var width = ref(isClient ? window.innerWidth : initialWidth);
    var height = ref(isClient ? window.innerHeight : initialHeight);
    if (isClient) {
        useEventListener('resize', function () {
            width.value = window.innerWidth;
            height.value = window.innerHeight;
        });
    }
    return { width: width, height: height };
}

var version = '2.0.18';

export { useAsyncState, useBattery, useBrowserLocation, useClipboard, useCounter, useCssVar, useDeviceLight, useDeviceMotion, useDeviceOrientation, useEventListener, useFullscreen, useGeolocation, useIdle, useInterval, useIntervalFn, useLocalStorage, useMediaQuery, useMouse, useMouseInElement, useNetwork, useNow, useOnline, usePageLeave, useParallax, usePermission, usePreferredColorScheme, usePreferredDark, usePreferredLanguages, useRaf, useRafFn, useSessionStorage, useShare, useStorage, useStoragePlain, useTimeout, useTimeoutFn, useWebSocket, useWebWorker, useWindowScroll, useWindowSize, version };
